import React from 'react';
import PropTypes from 'prop-types';
import { ProductCardCRO, RecommendedProductCard } from '@web/molecules';
import { toggleItem } from '../../toggle';
import {
  PRICE_PARITY_STARTING_AT_TEXT_KEY,
  useExperiment,
} from '../../experiments';
import { ProductSelectionContainer, ProductCardWrapper } from './styles.js';

export const ProductSelector = ({
  products,
  unavailableProducts = [],
  selected = [],
  onChange,
  onDetailsClick,
  variant = 'checkbox',
  productCardVariant = 'recommended',
  productCardBackgroundColor = 'base',
  notSelectableText,
  gapSize = 4,
  mobileGapSize,
  mobileGridColumns = 2,
  gridColumns = 3,
  cardOrientation = 'row',
  mobileCardOrientation,
  stackOnMobile = true,
  showPricePerWeek = true,
  priceText,
  offsetBadge = false,
  showCheckmark,
  imageAlign,
  cardWidth,
  cardHeight,
  cardTypography,
  cardHeightMobile,
  cardWidthMobile,
  badgeVariant,
  cardImgSize,
  mobileDescriptionPosition,
  alignColumnContent,
  cardBorderRadius,
}) => {
  const handleChange = (selected, product) => {
    variant === 'checkbox'
      ? onChange?.(toggleItem(selected, product))
      : !selected.includes(product) && onChange?.([product]);
  };

  const { value: showPriceText } = useExperiment(
    PRICE_PARITY_STARTING_AT_TEXT_KEY,
    false,
  );

  const getProductCard = (cardVariant, props) => {
    if (cardVariant === 'cro') {
      return (
        <ProductCardCRO
          {...props}
          height={cardHeight}
          showPricePerWeek={showPricePerWeek}
          priceText={priceText}
          imageAlign={imageAlign}
          mobileDescriptionPosition={mobileDescriptionPosition}
          showPriceText={showPriceText}
        />
      );
    } else {
      return (
        <RecommendedProductCard
          {...props}
          badge={props.benefit || props.badge}
          outOfStock={props.outOfStock}
          typography={cardTypography}
          offsetBadge={offsetBadge}
          badgeVariant={badgeVariant}
          imgSize={cardImgSize}
          orientation={cardOrientation}
          mobileOrientation={mobileCardOrientation}
          alignColumnContent={alignColumnContent}
          borderRadius={cardBorderRadius}
          mobileWidth={cardWidthMobile}
        />
      );
    }
  };

  return (
    <ProductSelectionContainer
      $mobileGridColumns={mobileGridColumns}
      $gridColumns={products.length === 4 ? 2 : gridColumns}
      $gapSize={gapSize}
      $mobileGapSize={mobileGapSize ?? gapSize}
      $stackOnMobile={stackOnMobile}
    >
      {products.map(product => {
        const isUnavailable = unavailableProducts.includes(product.value);
        const notSelectable =
          isUnavailable ||
          (selected.length === 4 && !selected.includes(product.value));

        const cardProps = {
          ...product,
          unavailable: isUnavailable,
          notSelectable,
          notSelectableText,
          onDetailsClick: () => onDetailsClick?.(product),
          onChange: e => handleChange(selected, product.value),
          checked: selected.includes(product.value),
          width: cardWidth,
          bgColor: productCardBackgroundColor,
          mobileHeight: cardHeightMobile,
          showCheckmark: showCheckmark,
        };
        return (
          <ProductCardWrapper key={product.value}>
            {getProductCard(productCardVariant, cardProps)}
          </ProductCardWrapper>
        );
      })}
    </ProductSelectionContainer>
  );
};

ProductSelector.displayName = 'ProductSelector';
ProductSelector.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
    }),
  ),
  unavailableProducts: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onDetailsClick: PropTypes.func,
  variant: PropTypes.oneOf(['checkbox', 'radio']),
  productCardVariant: PropTypes.oneOf(['cro', 'recommended']),
  productCardBackgroundColor: PropTypes.oneOf(['base', 'lightest']),
  cardOrientation: PropTypes.oneOf(['row', 'column']),
  mobileCardOrientation: PropTypes.oneOf(['row', 'column']),
  notSelectableText: PropTypes.string.isRequired,
  gapSize: PropTypes.number,
  mobileGapSize: PropTypes.number,
  stackOnMobile: PropTypes.bool,
  showPricePerWeek: PropTypes.bool,
  priceText: PropTypes.string,
  cardWidth: PropTypes.number,
  cardHeight: PropTypes.number,
  imageAlign: PropTypes.string,
  mobileDescriptionPosition: PropTypes.oneOf(['bottom', 'right']),
  showCheckmark: PropTypes.bool,
  cardImgSize: PropTypes.number,
  cardHeightMobile: PropTypes.number,
  cardWidthMobile: PropTypes.number,
  badgeVariant: PropTypes.string,
  alignColumnContent: PropTypes.string,
  gridColumns: PropTypes.number,
  mobileGridColumns: PropTypes.number,
  cardBorderRadius: PropTypes.number,
};
